import React, { useEffect, useState } from "react";
import {
  useAddAppToCollectionMutation,
  useFetchAppCollectionsMutation,
  useRemoveAppsFromCollectionMutation,
} from "@/features/auth/api/authAPI";
import styles from "./AddToCollectionModal.module.scss";
import { CheckIcon, PlusIcon, XIcon } from "lucide-react";
import Loader from "@/shared/ui/Loader/Loader";
import { AppCollection } from "@/features/auth/types/auth";
import CreateCollectionModal from "./CreateCollectionModal";

interface AddToCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  appId: number;
  handleRemoveFromCollection?: (collectionId: number) => void;
}

const AddToCollectionModal: React.FC<AddToCollectionModalProps> = ({
  isOpen,
  onClose,
  appId,
  handleRemoveFromCollection,
}) => {
  const [fetchAppCollections, { isLoading }] = useFetchAppCollectionsMutation();
  const [addAppToCollection, { isLoading: isAdding }] =
    useAddAppToCollectionMutation();
  const [removeAppsFromCollection, { isLoading: isRemoving }] =
    useRemoveAppsFromCollectionMutation();

  const [collections, setCollections] = useState<AppCollection[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const loadCollections = async () => {
    const res = await fetchAppCollections();
    setCollections(res.data?.results || []);
  };

  useEffect(() => {
    if (isOpen) {
      loadCollections();
    }
  }, [isOpen]);

  const handleAddAppToCollection = async (collectionId: number) => {
    // Check if the app is already in the specific collection being clicked
    const targetCollection = collections.find(
      (collection) => collection.id === collectionId,
    );

    if (targetCollection && targetCollection.apps_pks.includes(appId)) {
      // Optimistic UI update: Remove app from collection
      setCollections((prevCollections) =>
        prevCollections.map((collection) =>
          collection.id === collectionId
            ? {
                ...collection,
                apps_pks: collection.apps_pks.filter((id) => id !== appId),
                total_apps: collection.total_apps - 1,
              }
            : collection,
        ),
      );

      try {
        await removeAppsFromCollection({ id: collectionId, apps: [appId] });
        handleRemoveFromCollection?.(collectionId);
      } catch (error) {
        console.error("Failed to remove app from collection", error);
        // Revert optimistic update on error
        setCollections((prevCollections) =>
          prevCollections.map((collection) =>
            collection.id === collectionId
              ? {
                  ...collection,
                  apps_pks: [...collection.apps_pks, appId],
                  total_apps: collection.total_apps + 1,
                }
              : collection,
          ),
        );
      }
      return;
    }

    // Optimistic UI update: Add app to collection
    setCollections((prevCollections) =>
      prevCollections.map((collection) =>
        collection.id === collectionId
          ? {
              ...collection,
              apps_pks: [...collection.apps_pks, appId],
              total_apps: collection.total_apps + 1,
            }
          : collection,
      ),
    );

    try {
      await addAppToCollection({ id: collectionId, apps: [appId] });
      // onClose();
    } catch (error) {
      console.error("Failed to add app to collection", error);
      // Revert optimistic update on error
      setCollections((prevCollections) =>
        prevCollections.map((collection) =>
          collection.id === collectionId
            ? {
                ...collection,
                apps_pks: collection.apps_pks.filter((id) => id !== appId),
                total_apps: collection.total_apps - 1,
              }
            : collection,
        ),
      );
    }
  };

  const handleCreateNewClick = () => {
    setShowCreateModal(true);
  };

  const handleCreateModalClose = () => {
    setShowCreateModal(false);
  };

  const handleCollectionCreated = () => {
    loadCollections(); // Refresh the collections list
    onClose(); // Close the main modal
  };

  if (!isOpen) return null;

  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Show CreateCollectionModal if it's open
  if (showCreateModal) {
    return (
      <CreateCollectionModal
        isOpen={showCreateModal}
        onClose={handleCreateModalClose}
        appId={appId}
        onCollectionCreated={handleCollectionCreated}
      />
    );
  }

  return (
    <div className={styles.modal} onClick={handleModalClick}>
      <div className={styles.header}>
        <h3>Add to Collection</h3>
        <button className={styles.closeButton} onClick={onClose}>
          <XIcon color="#BABABA" width={16} height={16} />
        </button>
      </div>
      <div className={styles.dividerContainer}>
        <div className={styles.divider} />
      </div>
      <div className={styles.content}>
        <div className={styles.collectionsContainer}>
          {isLoading ? (
            <div className={styles.loading}>
              <Loader />
            </div>
          ) : (
            <>
              {collections?.map((collection) => {
                const isAppInCollection = collection.apps_pks.includes(appId);
                return (
                  <div
                    key={collection.id}
                    className={styles.collectionItem}
                    onClick={() => handleAddAppToCollection(collection.id)}
                  >
                    <span>{collection.name}</span>
                    {isAppInCollection && (
                      <div className={styles.appInCollection}>
                        <CheckIcon color="#fff" width={16} height={16} />
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
        <div className={styles.createNewButtonContainer}>
          <button
            className={styles.createNewButton}
            onClick={handleCreateNewClick}
            disabled={isAdding || isRemoving}
          >
            <PlusIcon color="#fff" width={16} height={16} />
            <span>Create New Collection</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddToCollectionModal;
